/**
 * Secure token storage utility for Artify dashboard
 * Provides secure storage for authentication tokens and sensitive data
 */

export interface SecureStorageItem {
	value: string;
	expiresAt?: number;
}

export interface SecureStorageConfig {
	prefix?: string;
	encryptionKey?: string;
}

/**
 * Secure storage implementation using browser storage with encryption fallback
 */
export class SecureStorage {
	private prefix: string;
	private encryptionKey: string;

	constructor(config: SecureStorageConfig = {}) {
		this.prefix = config.prefix || "artify_";
		this.encryptionKey = config.encryptionKey || "artify_default_key";
	}

	private getKey(key: string): string {
		return `${this.prefix}${key}`;
	}

	private encrypt(value: string): string {
		// Simple XOR encoding with key for demo - in production, use proper encryption
		try {
			const keyChar = this.encryptionKey.charCodeAt(0) % 256;
			const encoded = Array.from(value)
				.map((char) => String.fromCharCode(char.charCodeAt(0) ^ keyChar))
				.join("");
			return btoa(encoded);
		} catch {
			return btoa(value);
		}
	}

	private decrypt(value: string): string {
		// Simple XOR decoding with key for demo - in production, use proper decryption
		try {
			const decoded = atob(value);
			const keyChar = this.encryptionKey.charCodeAt(0) % 256;
			return Array.from(decoded)
				.map((char) => String.fromCharCode(char.charCodeAt(0) ^ keyChar))
				.join("");
		} catch {
			try {
				return atob(value);
			} catch {
				return value;
			}
		}
	}

	setItem(key: string, value: string, expiresInMinutes?: number): void {
		const item: SecureStorageItem = {
			value: this.encrypt(value),
		};

		if (expiresInMinutes) {
			item.expiresAt = Date.now() + expiresInMinutes * 60 * 1000;
		}

		localStorage.setItem(this.getKey(key), JSON.stringify(item));
	}

	getItem(key: string): string | null {
		try {
			const itemStr = localStorage.getItem(this.getKey(key));
			if (!itemStr) return null;

			const item: SecureStorageItem = JSON.parse(itemStr);

			// Check expiration
			if (item.expiresAt && Date.now() > item.expiresAt) {
				this.removeItem(key);
				return null;
			}

			return this.decrypt(item.value);
		} catch {
			return null;
		}
	}

	removeItem(key: string): void {
		localStorage.removeItem(this.getKey(key));
	}

	clear(): void {
		const keys = Object.keys(localStorage);
		keys.forEach((key) => {
			if (key.startsWith(this.prefix)) {
				localStorage.removeItem(key);
			}
		});
	}
}

/**
 * Token manager for authentication tokens
 */
export class TokenManager {
	private storage: SecureStorage;
	private readonly TOKEN_KEY = "auth_token";

	constructor() {
		this.storage = new SecureStorage({
			prefix: "artify_secure_",
			encryptionKey: "artify_dashboard_key_v1",
		});
	}

	setToken(token: string, expiresInMinutes: number = 480): void {
		this.storage.setItem(this.TOKEN_KEY, token, expiresInMinutes);
	}

	getToken(): string | null {
		return this.storage.getItem(this.TOKEN_KEY);
	}

	removeToken(): void {
		this.storage.removeItem(this.TOKEN_KEY);
	}

	isTokenExpired(): boolean {
		return this.getToken() === null;
	}
}

// Create the configured instances that the app expects
const secureStorage = new SecureStorage({
	encryptionKey: "artify_dashboard_key_v1",
	prefix: "artify_secure_",
});

export { secureStorage };
